import { navigate, parseHash, type Route } from "./router.ts";
import { parentRoute } from "./back-swipe.ts";
import { openLayerCount } from "./escape.ts";
import { hasTypedText } from "./typed-text.ts";

/**
 * The keyboard's way round the app on a desktop (owner, 2026-10-05: "A lot of interactions only make
 * sense on mobile"). Two things a thumb never needs:
 *
 *  - Scrolling by keys. The document never scrolls; each screen's content pane (`main.content`)
 *    does, and Space, the page keys, the arrows, Home and End scroll only the focused scroller. So
 *    the pane holds the focus (tabindex -1, no ring drawn): src/app.tsx gives it the focus when a
 *    screen mounts or the route changes, and a scroll key pressed while the focus has fallen to the
 *    page body (a lazy screen replacing its spinner, a viewer that closed) hands it to the pane
 *    before the browser scrolls, so the press is never lost.
 *  - Escape as Back. With no sheet, viewer, tour or composer open (those close first, top down,
 *    src/escape.ts) and no field being typed in, Escape goes up one level, to the same screen as
 *    the Back arrow and the phone's edge swipe (src/back-swipe.ts parentRoute), unless the screen
 *    holds something typed (a draft task's title), which one stray key must not throw away. On a
 *    tab it does nothing.
 *
 * The phone's edge swipe stays the phone's (src/back-swipe.ts); nothing here listens to touch.
 */

/** The keys a scroller answers. */
const SCROLL_KEYS = new Set([" ", "Spacebar", "PageDown", "PageUp", "ArrowDown", "ArrowUp", "Home", "End"]);

export function isScrollKey(key: string): boolean {
  return SCROLL_KEYS.has(key);
}

/** Inputs that take typing (Escape belongs to them); a checkbox, a radio or a button does not. */
const NOT_TYPED = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "file", "color", "image"]);

/** True when `el` is a field being typed in or chosen from: a text input, a textarea, a select, or editable text. */
export function isTypingField(el: { tagName?: string; type?: string; isContentEditable?: boolean } | null | undefined): boolean {
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toUpperCase();
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") return !NOT_TYPED.has((el.type ?? "text").toLowerCase());
  return el.isContentEditable === true;
}

export interface EscapeState {
  route: Route;
  /** Open Escape layers (src/escape.ts openLayerCount): a sheet, a viewer, the tour, a composer. */
  layers: number;
  /** The focus is in a field being typed in. */
  field: boolean;
  /** A modal dialog is in the page, layer or not. */
  modal: boolean;
  /** Something is typed into a field on the screen (a draft title): a stray Escape never throws it away; Back still can. */
  typed: boolean;
}

/** Where a bare Escape goes: one level up from a sub-screen, or nowhere (a tab, or something else answers first). */
export function escapeUp(state: EscapeState): Route | null {
  if (state.layers > 0 || state.field || state.modal || state.typed) return null;
  return parentRoute(state.route);
}

/** The screen's content pane, the one scroller a screen has. */
export function contentPane(): HTMLElement | null {
  return document.querySelector<HTMLElement>(".screen main.content");
}

/**
 * Gives the content pane the focus, without scrolling it or drawing a ring (styles.css), unless the
 * focus is already somewhere that wants it: inside the pane (a form's autofocus field), in a field,
 * or in a sheet, a viewer or the tour. Returns whether it moved the focus.
 */
export function focusContentPane(): boolean {
  const pane = contentPane();
  if (!pane || !pane.isConnected) return false;
  const active = document.activeElement;
  if (active instanceof Element && active !== document.body && active !== document.documentElement) {
    if (pane.contains(active) || isTypingField(active as HTMLElement) || active.closest("[role='dialog'], [aria-modal='true']")) return false;
  }
  if (document.querySelector("[aria-modal='true']")) return false;
  if (!pane.hasAttribute("tabindex")) pane.tabIndex = -1;
  pane.focus({ preventScroll: true });
  return document.activeElement === pane;
}

/** True while nothing in particular holds the focus: the page body, or the root. */
function focusIsLost(): boolean {
  const a = document.activeElement;
  return !a || a === document.body || a === document.documentElement;
}

function onKey(e: KeyboardEvent): void {
  if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.key === "Escape") {
    const target = escapeUp({
      route: parseHash(location.hash),
      layers: openLayerCount(),
      field: isTypingField(document.activeElement as HTMLElement | null),
      modal: !!document.querySelector("[aria-modal='true']"),
      typed: hasTypedText(contentPane()),
    });
    if (!target) return;
    e.preventDefault();
    navigate(target, true);
    return;
  }
  // The pane takes the focus now; the browser's own scroll for this press then lands on it.
  if (isScrollKey(e.key) && focusIsLost()) focusContentPane();
}

let installed = false;

/** Installs the keys once, for the page's life (src/app.tsx). */
export function installDesktopKeys(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("keydown", onKey);
}
